import { Tick } from "@asphalt-react/iconpack";
import * as React from "react";
import { Box } from "src/components/Box";
import { Text } from "src/components/Text";
import styled, { css, keyframes } from "styled-components";
import { withStyles } from "src/decorators";
import { useTranslation } from "src/hooks";
import { observer } from "mobx-react-lite";

type IStepperProps = React.HTMLProps<HTMLInputElement> & {
  activeIndex?: number;
  isCompleted?: boolean;
  vertical?: boolean;
  combine?: boolean;
};

type IStepperItemProps = React.HTMLProps<HTMLInputElement> & {
  active?: boolean;
  text?: any;
  completed?: boolean;
  vertical?: boolean;
};

const StepperEl = styled.div<{ vertical: any }>`
  display: flex;
  overflow: hidden;
  justify-content: center;
  padding: 16px;

  ${(p) =>
    p.vertical &&
    css`
      flex-direction: column;
    `}
`;

const StepperItemEl = styled.div<IStepperItemProps>`
  font-size: ${(p) => p.theme.text.size.small};
  text-align: center;
  position: relative;
  width: 30%;

  ${(p) =>
    p.vertical &&
    css`
      height: 72px;
      width: 100%;
    `}

  &:before {
    background-color: ${(p) => p.theme.colors.background.accent};
    content: "";
    position: absolute;
    top: 13px;

    ${(p) =>
      p.vertical
        ? css`
            border-left-style: solid;
            border-left-width: 4px;
            bottom: 0;
            color: ${p.theme.colors.borders.appBar};
            left: 12px;
            position: absolute;
            top: 0;
          `
        : css`
            height: 4px;
            width: 100%;
          `}

    ${(p) =>
      p.completed &&
      css`
        background-color: ${p.theme.colors.background.primaryLight};
        color: ${p.theme.colors.background.primaryLight};
      `}
  }

  &:after {
    color: ${(p) => p.theme.colors.text.placeholder};
    content: "${(p) => p.text}";
    font-family: ${(p) => p.theme.text.fontFamily.book};
    font-size: ${(p) => p.theme.text.size.small};

    ${(p) =>
      p.vertical &&
      css`
        position: absolute;
        top: 7px;
        left: 38px;
        font-family: ${(p) => p.theme.text.fontFamily.demi};
        font-size: ${(p) => p.theme.text.size.normal};
      `}

    ${(p) =>
      p.active &&
      css`
        color: ${(p) => p.theme.colors.text.readable};
      `}
  }

  &:last-child {
    &:before {
      display: none;
    }
  }
`;

const StepperIconEl = styled.div<IStepperItemProps>`
  align-items: center;
  background-color: ${(p) => p.theme.colors.background.white};
  border-radius: 50%;
  box-shadow: 0 3px 9px -2px rgba(0, 0, 0, 0.25);
  color: ${(p) => p.theme.colors.background.white};
  display: flex;
  height: 28px;
  justify-content: center;
  line-height: 30px;
  margin: 0 auto 10px auto;
  text-align: center;
  position: relative;
  width: 28px;

  ${(p) =>
    p.vertical &&
    css`
      margin: 0;
    `}

  ${(p) =>
    p.active &&
    css`
      color: ${(p) => p.theme.colors.background.white};
    `}

  ${(p) =>
    p.completed &&
    css`
      background-color: ${(p) => p.theme.colors.background.primary};
    `}
`;

const RippleAnimation = keyframes`
  0% {
    opacity: 1;
    transform: scale(0);
  }
  100% {
    opacity: 0;
    transform: scale(2);
  }
`;

const InProgressIconEl = styled.div`
  &:before,
  &:after {
    background: ${(p) => p.theme.colors.background.primary};
    border-radius: 50%;
    content: "";
    height: 10px;
    left: 0;
    margin-left: auto;
    margin-right: auto;
    position: absolute;
    right: 0;
    top: 8px;
    text-align: center;
    width: 10px;
  }

  &:after {
    animation: ${RippleAnimation};
    animation-duration: 1.8s;
    animation-iteration-count: infinite;
  }
`;

const CombinedStepperContainerEl = styled(Box)`
  align-items: center;
  display: flex;
  ${(p) =>
    css`
      border: 1px solid ${p.theme.colors.borders.stepper};
      border-radius: ${p.theme.dimension.borderRadius[1]}px;
      padding: ${p.theme.layout.margin.s}px ${p.theme.layout.margin.l}px;
    `}
`;

export const StepperItem = (props: IStepperItemProps) => {
  const { active, completed, vertical, text } = props;

  const iconState = () => {
    if (completed) {
      return <Tick />;
    }
    if (active) {
      return <InProgressIconEl />;
    }
    return;
  };

  return (
    <StepperItemEl
      active={active}
      completed={completed}
      vertical={vertical}
      text={text}
    >
      <StepperIconEl active={active} completed={completed} vertical={vertical}>
        {iconState()}
      </StepperIconEl>
    </StepperItemEl>
  );
};

export const Stepper = observer(
  withStyles((props: IStepperProps) => {
    const { children, activeIndex = 0, vertical, combine, className } = props;
    const injectChildrenProps = React.Children.map(
      children,
      (child: any, index) =>
        React.cloneElement(child, {
          ...child.props,
          active: index <= activeIndex,
          completed: index < activeIndex,
          vertical,
        })
    );

    const translate = useTranslation(require("./translations.json"));

    if (combine) {
      const totalChild = React.Children.count(children);
      const getActiveChild = React.Children.toArray(children)[activeIndex];
      const currentNumber = activeIndex + 1;
      let text = "";

      if (React.isValidElement<IStepperItemProps>(getActiveChild)) {
        text = getActiveChild.props.text;
      }

      return (
        <CombinedStepperContainerEl width100>
          <Box>
            <Text fontWeight="demi" fontSize="small">
              {translate("steps")} {currentNumber}
              <Text
                as="span"
                fontWeight="demi"
                color="placeholder"
                fontSize="small"
              >
                {" "}
                / {totalChild}
              </Text>
            </Text>
            <Text fontWeight="demi">{text}</Text>
          </Box>
        </CombinedStepperContainerEl>
      );
    }

    return (
      <Box marginBottomL className={className}>
        <StepperEl vertical={vertical}>{injectChildrenProps}</StepperEl>
      </Box>
    );
  })
);
